import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

// third party
import { useSelector, useDispatch } from 'react-redux';

// project import
import * as actionTypes from 'store/actions';

// assets
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// ==============================|| NAV ITEM ||============================== //

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const dispatch = useDispatch();
  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon color="inherit" /> : <ArrowForwardIcon color="inherit" fontSize={level > 0 ? 'inherit' : 'default'} />;

  let itemTarget = '';
  if (item.target) {
    itemTarget = '_blank';
  }
  let listItemProps = { component: Link, to: item.url };
  if (item.external) {
    listItemProps = { component: 'a', href: item.url };
  }

  const isSelected = customization.isOpen === item.id;

  return (
    <ListItemButton
      disabled={item.disabled}
      sx={{
        ...(level > 1 && { 
          backgroundColor: 'transparent !important', 
          py: 1, 
          borderRadius: '12px',
          mx: 1
        }),
        borderRadius: '12px',
        marginBottom: '8px',
        // Match NavCollapse: no horizontal margin for top-level (level 1)
        mx: level > 1 ? 1 : 0,
        // Match left padding with collapse items for consistent alignment
        pl: `${level * 16}px`,
        pr: 2,
        py: 1.5,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
        border: isSelected ? '1px solid rgba(25, 118, 210, 0.2)' : '1px solid transparent',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: isSelected ? 4 : 0,
          background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
          borderRadius: '0 2px 2px 0',
          transition: 'width 0.3s ease'
        },
        '&:hover': {
          backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.04)',
          border: '1px solid rgba(25, 118, 210, 0.15)',
          transform: 'translateX(4px)',
          '&::before': {
            width: 4
          }
        }
      }}
      selected={isSelected}
      component={Link}
      onClick={() => dispatch({ type: actionTypes.MENU_OPEN, isOpen: item.id })}
      to={item.url}
      target={itemTarget}
      {...listItemProps}
    >
      <ListItemIcon sx={{ 
        // Match icon minWidth behavior with collapse for alignment
        minWidth: !item.icon ? '25px' : 'unset',
        color: isSelected 
          ? '#1976d2' 
          : theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.87)' 
            : 'rgba(0, 0, 0, 0.6)',
        transition: 'color 0.3s ease'
      }}>
        {itemIcon}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography sx={{ 
            // Match text left padding with collapse
            pl: 1.9,
            fontWeight: isSelected ? 600 : 500,
            color: isSelected 
              ? '#1976d2' 
              : theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.87)' 
                : 'rgba(0, 0, 0, 0.8)',
            fontSize: '0.9rem',
            transition: 'all 0.3s ease'
          }}>
            {item.title}
          </Typography>
        }
        secondary={
          item.caption && (
            <Typography variant="caption" sx={{ 
              ...theme.typography.subMenuCaption, 
              pl: 2,
              color: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.6)' 
                : 'rgba(0, 0, 0, 0.6)',
              fontSize: '0.75rem'
            }} display="block" gutterBottom>
              {item.caption}
            </Typography>
          )
        }
      />
      {item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          sx={{
            fontSize: '0.7rem',
            height: 20,
            '& .MuiChip-label': {
              px: 1
            }
          }}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number,
  icon: PropTypes.object,
  target: PropTypes.object,
  url: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  title: PropTypes.string,
  caption: PropTypes.string,
  chip: PropTypes.object,
  color: PropTypes.string,
  label: PropTypes.string,
  avatar: PropTypes.object
};

export default NavItem;
